import React, { useEffect, useRef } from 'react';

const SIGNWELL_EMBED_SCRIPT_SRC = 'https://static.signwell.com/assets/embedded.js';

const ensureSignwellEmbedScript = () => new Promise((resolve, reject) => {
  if (typeof window === 'undefined') {
    reject(new Error('Window is not available'));
    return;
  }
  if (window.SignWellEmbed) {
    resolve(window.SignWellEmbed);
    return;
  }
  const existing = document.querySelector(`script[src="${SIGNWELL_EMBED_SCRIPT_SRC}"]`);
  if (existing) {
    existing.addEventListener('load', () => resolve(window.SignWellEmbed), { once: true });
    existing.addEventListener('error', () => reject(new Error('Failed to load SignWell embed script')), { once: true });
    return;
  }
  const script = document.createElement('script');
  script.src = SIGNWELL_EMBED_SCRIPT_SRC;
  script.async = true;
  script.onload = () => resolve(window.SignWellEmbed);
  script.onerror = () => reject(new Error('Failed to load SignWell embed script'));
  document.head.appendChild(script);
});

const SmsAgreementView = ({
  onStartSigning,
  onCancel,
  onRefreshStatus,
  onSigningSessionEnd,
  signingUrl,
  isStarting = false,
  errorMessage = '',
  isSigned = false,
}) => {
  const openedSigningUrlRef = useRef('');

  useEffect(() => {
    if (!signingUrl || openedSigningUrlRef.current === signingUrl) {
      return undefined;
    }
    openedSigningUrlRef.current = signingUrl;
    let isActive = true;
    const completeAndRefresh = async () => {
      if (!isActive) return;
      if (typeof onSigningSessionEnd === 'function') {
        onSigningSessionEnd();
      }
      if (typeof onRefreshStatus === 'function') {
        await onRefreshStatus();
      }
    };
    ensureSignwellEmbedScript()
      .then((SignWellEmbedCtor) => {
        if (!isActive || typeof SignWellEmbedCtor !== 'function') {
          throw new Error('SignWell embed library not available');
        }
        const signWellEmbed = new SignWellEmbedCtor({
          url: signingUrl,
          events: {
            completed: completeAndRefresh,
            closed: completeAndRefresh,
            error: completeAndRefresh,
          },
        });
        signWellEmbed.open();
      })
      .catch(() => {
        completeAndRefresh();
        window.open(signingUrl, '_blank', 'noopener,noreferrer');
      });
    return () => {
      isActive = false;
    };
  }, [signingUrl, onRefreshStatus, onSigningSessionEnd]);

  if (isSigned) {
    return (
      <div className="max-w-3xl mx-auto rounded-2xl shadow-xl p-6 md:p-8 my-6 border border-green-200 bg-green-50">
        <h2 className="text-2xl md:text-3xl font-bold text-green-900 mb-4">
          SMS Notifications Consent
        </h2>
        <p className="text-sm text-green-800 mb-6">
          You have already signed the SMS consent. No further action is needed here.
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-green-700 text-white hover:bg-green-800 text-sm font-medium min-h-[44px]"
        >
          Return to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-6 md:p-8 my-6">
      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
        SMS Notifications Consent
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        Review and sign the SMS consent through our secure e-sign flow. Click
        <span className="font-medium"> Start Secure E-sign </span>
        to continue.
      </p>
      <div className="flex flex-col sm:flex-row justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium min-h-[44px]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onStartSigning}
          disabled={isStarting}
          className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-yellow-600 text-white hover:bg-yellow-700 text-sm font-medium min-h-[44px]"
        >
          {isStarting ? 'Starting secure signing...' : 'Start Secure E-sign'}
        </button>
      </div>
      {errorMessage && (
        <p className="mt-3 text-sm text-red-600">{errorMessage}</p>
      )}
      {signingUrl && (
        <div className="mt-4 border rounded-xl p-4 bg-gray-50">
          <p className="text-sm text-gray-700">
            Secure signing window opened. Complete signing there and we will refresh your status.
          </p>
        </div>
      )}
    </div>
  );
};

export default SmsAgreementView;
